import { PaginatedDataDTO, PaginationDTO } from '@bottega52/commons-pagination';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import { DeleteOutline, SearchOutlined } from '@material-ui/icons';
import { Button } from '@mui/material';
import _ from 'lodash';
import { Stack } from '@mui/system';
import { DataGrid, GridColumns, GridRenderCellParams } from '@mui/x-data-grid';
import * as React from "react";
import { PERMISSIONS, Roles } from '../../services/PermissionService/PermissionConstants';
import translations from '../../translations/i18next';
import ReduxLanguage from '../ReduxLanguage';
import Permission from '../Permission/Permission';
import { IDemoPlantInDTO } from '../../repository/jago/model/input/IDemoPlantInDTO';
import AbilityProvider from '../../services/PermissionService/AbilityProvider';
import { Chip } from '@material-ui/core';
import { getDomainURLFromHostname } from '../../repository/utils';

const styles = createStyles({
  rowContainer: {
    display: 'flex',
    margin: 10,
    flexDirection: 'column'
  },
  rowBodyContainer: {
    padding: 15,
    minHeight: 100,
  },
  rowTitle: {
    fontWeight: 'bold'
  },
  actionButton: {
    textTransform: 'none',
    minWidth: 100
  }
});

export interface IUserDemoPlantsTableState {
}

export interface IUserDemoPlantsTableProps extends WithStyles<typeof styles>{
  demoPlants: PaginatedDataDTO<IDemoPlantInDTO>
  tableColumns?: GridColumns<IDemoPlantInDTO>;
  onRowClick?: (demoPlant: IDemoPlantInDTO) => void;
  onFetchElementsOnPage: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onOpenDomain: (domain: IDemoPlantInDTO) => void;
  onDeleteDomain: (domain: IDemoPlantInDTO) => void;
  rowsPerPage: number;
  rowHeight?: number;
  disableSelectionOnClick?: boolean;
  isLoading?: boolean;
}

class UserDemoPlantsTable extends React.Component<IUserDemoPlantsTableProps, IUserDemoPlantsTableState> {
  
  public render() {
    const { demoPlants, rowsPerPage, onDeleteDomain, disableSelectionOnClick, onRowClick, onPageSizeChange, onFetchElementsOnPage, tableColumns, rowHeight, onOpenDomain } = this.props;
    const isSofiaAdmin = AbilityProvider.getAbilityHelper().hasRoles([Roles.ROLE_VASHO_SOFIA_ADMIN]);
    const noteColumn: GridColumns<IDemoPlantInDTO>[number] = {
      field: 'note',
      headerName: translations.t('demoSystem.notes'),
      type: 'string',
      sortable: true,
      filterable: true,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<any>) => {
        const demoPlant = params.row;
        return (
          <div>
            <span>{demoPlant.note || '-'}</span>
          </div>
        );
      }
    };

    const defaultColumns: GridColumns<IDemoPlantInDTO> = [
      {
        field: 'name',
        headerName: translations.t('demoSystem.name'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 350,
        renderCell: (params: GridRenderCellParams<any>) => {
          const demoPlant = params.row;
          return (
            <div>
              <h3 style={{ margin: 0, fontWeight: 'bold' }}>
                {demoPlant.name}
              </h3>
            </div>
          )
        }
      },
      ...(isSofiaAdmin ? [noteColumn] : []),
      {
        field: 'url',
        headerName: translations.t('demoSystem.url'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 300,
        renderCell: (params: GridRenderCellParams<any>) => {
          const demoPlant = params.row;
          const plant = demoPlant.plant;
          const hostname = plant && plant.hostname ? getDomainURLFromHostname(plant.hostname) : '-';
          return (
            <div>
              <a href={hostname} target="_blank" rel="noopener noreferrer">{hostname}</a>
            </div>
          );
        }
      },
      {
        field: 'peopleUsing',
        headerName: translations.t('demoSystem.peopleUsing'),
        type: 'string',
        sortable: true,
        filterable: true,
        minWidth: 180,
        flex: isSofiaAdmin ? 0 : 0.8,
        renderCell: (params: GridRenderCellParams<any>) => {
          const demoPlant = params.row;
          const user = demoPlant.user;
          if (demoPlant.connectedUsers && !_.isEmpty(demoPlant.connectedUsers)) {
            return (
              <div>
                {_.map(demoPlant.connectedUsers, (connectedUser) => (
                  <Chip style={{ marginRight: 5 }} label={connectedUser.firstname + ' ' + connectedUser.lastname} />
                ))}
              </div>
            );
          }
          return (
            <div>
              <Chip label={user && user.firstname && user.lastname ? `${user.firstname} ${user.lastname}` : '0'} />
            </div>
          );
        }
      },
      {
        field: 'actions',
        headerName: translations.t('forms.actions'),
        type: 'actions',
        sortable: false,
        filterable: false,
        minWidth: 210,
        flex: isSofiaAdmin ? 0.2 : 0,
        renderCell: (params: GridRenderCellParams<any>) => {
          return (
            <div style={{ display: 'flex', flexDirection: 'row', gap: 10 }}>
            <Permission abilityHelper={AbilityProvider.getAbilityHelper()} permission={PERMISSIONS.DOMAIN_DEMO_READ}>
              <Button
                variant="contained"
                style={{
                  backgroundColor: 'green',
                  flexShrink: 0,
                  marginRight: 0,
                  width: 'fit-content',
                  color: 'white',
                  padding: 4,
                  paddingLeft: 9,
                  paddingRight: 10,
                  fontWeight: 'bold'
                }}
                onClick={(e) => {e.stopPropagation(); onOpenDomain(params.row)}}
              >
                <h5 style={{ fontWeight: 'bold', margin: 0, marginLeft: 5 }} ><ReduxLanguage languageKey={"demoSystem.openNow"} /></h5>
              </Button>
            </Permission>
            <Permission abilityHelper={AbilityProvider.getAbilityHelper()} permission={PERMISSIONS.DOMAIN_DEMO_DELETE}>
              <Button
                variant="contained"
                style={{
                  backgroundColor: 'darkred',
                  flexShrink: 0,
                  marginRight: 0,
                  width: 'fit-content',
                  color: 'white',
                  padding: 4,
                  paddingLeft: 10,
                  paddingRight: 10,
                  fontWeight: 'bold'}}
                onClick={(e) => {e.stopPropagation(); onDeleteDomain(params.row)}}
              >
                <DeleteOutline style={{ fontSize: 15 }}/>
                <h5 style={{ fontWeight: 'bold', margin: 0, marginLeft: 5 }} ><ReduxLanguage languageKey={"forms.delete"} /></h5>
              </Button>
            </Permission>
            </div>
          );
        }
      }
    ];

    return (
      <DataGrid
        getRowId={(d) => d.id}
        rows={demoPlants.content || []}
        columns={tableColumns ? tableColumns : defaultColumns}
        rowHeight={rowHeight ? rowHeight : 55}
        onPageSizeChange={(pageSize) => onPageSizeChange(pageSize)}
        onPageChange={page => onFetchElementsOnPage(page)}
        page={(demoPlants?.pagination as PaginationDTO)?.number}
        rowCount={(demoPlants?.pagination as PaginationDTO)?.totalElements}
        pageSize={rowsPerPage}
        loading={this.props.isLoading}
        onRowClick={row => onRowClick ? onRowClick(row.row) : null}
        paginationMode="server"
        disableSelectionOnClick={disableSelectionOnClick}
        componentsProps={{
          pagination: {
            labelRowsPerPage: translations.t('forms.rowsPerPage')
          }
        }}
        components={{
          NoRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                <SearchOutlined style={{ alignSelf: 'center' }} />
                <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}>
                  <ReduxLanguage languageKey='demoSystem.noDataTitle' />
                </h3>
                <h4 style={{ textAlign: 'center', marginTop: 0 }}>
                  <ReduxLanguage languageKey='demoSystem.noDataDescription' />
                </h4>
              </div>
            </Stack>
          ),
          NoResultsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              <h3 style={{ textAlign: 'center', fontWeight: 'bold', marginBottom: 0 }}>
                <ReduxLanguage languageKey='demoSystem.noDataTitle' />
              </h3>
              <h4 style={{ textAlign: 'center', marginTop: 0 }}>
                <ReduxLanguage languageKey='demoSystem.noDataDescription' />
              </h4>
            </Stack>
          )
        }}
        sx={{
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
      />
    );
  }
}

export default withStyles(styles)(UserDemoPlantsTable); 